<template>
    <div>
        <breadcrumbs />
        <div class="text-h5 font-weight-bold py-2">
            {{ campaign.name[activeLangCode] }}
        </div>
        <v-tabs v-model="selectedTabIndex" class="mb-4">
            <v-tab v-for="tab in tabs" :key="tab.name">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-window v-model="selectedTabIndex" class="overflow--visible">
            <v-window-item v-for="tab in tabs" :key="tab.name">
                <template v-if="tab.name === 'info'">
                    <campaign-form :campaign-data="campaign" />
                </template>
                <template v-if="tab.name === 'reservations'">
                    <orders-page
                        v-if="tab.name === 'reservations'"
                        ref="orders"
                        company-page-mode
                        @new-order-request-close="onNewOrderRequestClose"
                    >
                        <template v-slot:breadcrumbs><div /></template>
                        <template v-slot:agentsPaymentsUpload><div /></template>
                        <template v-slot:bookButtons>
                            <div class="d-flex align-center pb-4">
                                <v-btn data-cy="search-and-book" class="bg-primary" size="small" @click="searchAndBook">
                                    {{ $t('new_order') }}
                                </v-btn>
                                <v-btn
                                    v-if="$conf.public.account.newReservationRequest"
                                    size="small"
                                    class="bg-grey-lighten-4 ms-2"
                                    data-cy="new-order-request-for-client"
                                    @click="newOrderRequestForClient"
                                >
                                    {{ $t('new_reservation_request') }}
                                </v-btn>
                            </div>
                        </template>
                    </orders-page>
                </template>
            </v-window-item>
        </v-window>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import OrdersPage from '~/pages/account/OrdersPage.vue'
    import CampaignForm from '@/components/account/campaigns/CampaignForm.vue'
    import {initCampaign} from '~/utils/types/account/campaigns/helpers'

    const prepareData = data => {
        const keysToInclude = [
            'assignedUser',
            'type',
            'budgetValue',
            'budgetCurrency',
            'dateFrom',
            'dateTo',
            'status',
            'description',
            'name',
            'campaignId',
        ]
        const filteredObject = {
            dateFrom: null,
            dateTo: null,
        }
        for (const key in data) {
            if (keysToInclude.includes(key)) {
                switch (key) {
                    case 'assignedUser':
                        if (data[key].id) filteredObject[key] = data[key].id
                        break
                    default:
                        filteredObject[key] = data[key]
                }
            }
        }
        if (filteredObject.dateFrom) filteredObject.dateFrom = new Date(filteredObject.dateFrom)
        if (filteredObject.dateTo) filteredObject.dateTo = new Date(filteredObject.dateTo)

        return filteredObject
    }

    @Component({
        components: {OrdersPage, Breadcrumbs, CampaignForm},

        async setup() {
            const app = useNuxtApp()
            const $api = app.$api
            const $route = useRoute()
            const params = $route.params

            const {data} = await useAsyncData(`campaign-${params.id}`, async () => {
                if (!params.id) return initCampaign()
                try {
                    const {campaigns} = await $api.campaigns.get({campaignId: params.id, locale: 'all'})
                    if (campaigns.length) {
                        return prepareData(campaigns[0])
                    }
                    return initCampaign()
                } catch (e) {
                    return initCampaign()
                }
            })
            return {campaign: data.value}
        },
    })
    export class CampaignPage extends Vue {
        selectedTabIndex: number = 0
        activeLangCode: string = 'en'
        campaign = initCampaign()
        tempFilters = null

        async mounted() {
            this.activeLangCode = this.$i18n.locale
            this.tempFilters = persistentStore.orderFilters
            persistentStore.CLEAR_ORDER_FILTERS()
            persistentStore.SET_ORDER_FILTERS(
                Object.assign({}, persistentStore.orderFilters, {campaignId: this.campaign.campaignId})
            )
        }

        beforeUnmount() {
            persistentStore.CLEAR_ORDER_FILTERS()
        }

        get tabs() {
            let tabs: {[key: string]: string}[] = [{name: 'info', label: this.$t('information')}]

            if (+this.$route.params.id !== 0) {
                tabs = [...tabs, {name: 'reservations', label: this.$t('orders')}]
            }

            return tabs
        }

        onNewOrderRequestClose() {
            persistentStore.SET_SELECTED_COMPANY_AND_AGENT(null)
            persistentStore.SET_SELECTED_PRIVATE_CLIENT(null)
        }

        newOrderRequestForClient() {
            persistentStore.SET_SELECTED_PRIVATE_CLIENT({...authStore.person})
            if (this.$refs.orders?.[0]) {
                this.$refs.orders[0].newOrderRequestModal = true
            }
        }

        searchAndBook() {
            persistentStore.SET_SELECTED_PRIVATE_CLIENT({...authStore.person})
            this.$router.push({name: 'home'})
        }
    }

    export default CampaignPage
</script>
